/* LogsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Registrations = require("../../../../../RegistrationNamespaces");
const FileName = require("@gluenetworks/steve_utilities").fileName;
//const BaseLogsViewRenderer = require("../../../../lib/logs/BaseLogsViewRenderer");

class LogsViewRenderer /*extends BaseLogsViewRenderer*/ {
    constructor(model) {
        this._model = model;
        //super(model);
        this._rootElem = document.createElement("logs-view-gl");
        this._rootElem.component = this._model;
        this._logDisplay = document.createElement("log-display-gl");
        this._rootElem.appendChild(this._logDisplay);
        this._lineNumber = 0;
        //this._rootElem.appendChild(this._logsControl.renderer.getRootElement());
        this._logFile = "";
        this._logFileStarttime = undefined;
        this._onDownloadButtonClick = this._onDownloadButtonClick.bind(this);
        this._actionsRegistered = false;
        this._pendingLogs = [];
        this._flushScheduled = false;
        this._fileNamer = FileName;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._logDisplay = undefined;
            this._lineNumber = undefined;
            this._pendingLogs = undefined;
            this._flushScheduled = undefined;
            this._fileNamer = undefined;
            resolve();
        });
        return prom;
    }

    _registerViewActions() {
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Back",
            name: "back",
            handler: this._onBackButtonClick.bind(this)
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Download",
            name: "downloadLogs",
            handler: this._onDownloadButtonClick
        });
        this._model.gameEditor.enableViewAction("downloadLogs")
    }

    _registerListeners() {
        this._model.on("LogsInitialized", (this._onLogsInitialized = this._onLogsInitialized.bind(this)));
        this._model.on("LogAdded",(this._onLogAdded = this._onLogAdded.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeAllListeners("LogsInitialized");
        this._model.removeAllListeners("LogAdded");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    async render() {
        await this._model.whenModelIsReady();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onLogAdded(logEntry) {
        this._pendingLogs.push(logEntry);
        if (!this._flushScheduled) {
            this._flushScheduled = true;
            requestAnimationFrame(() => {
                this._flushPendingLogs();
            });
        }
    }

    _flushPendingLogs() {
        const logs = this._pendingLogs;
        this._pendingLogs = [];
        this._flushScheduled = false;

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < logs.length; i++) {
            this._lineNumber = this._lineNumber + 1;
            fragment.appendChild(this._createLogElement(logs[i], this._lineNumber));
        }
        this._logDisplay.appendChild(fragment);
    }

    _onLogsInitialized(pristineProps) {
        this._setLogs(pristineProps);
        this._logDisplay.scrollTop = this._logDisplay.scrollHeight;
    }

    _onDownloadButtonClick() {
        if (!_.isEmpty(this._logFile)) {
            this.model.gameEditor.disableViewAction("downloadLogs");
            let fileName = this._fileNamer({
                solution: "Schedules",
                general: "Log",
                specific: this.model.gameEditor.getTriggerType()?.name ?? "",
                date: this._logFileStarttime,
                suffix: "txt",
            });
            try {
                this.model.io.fileFactory.saveStringAsFile(this._logFile, fileName);
            } finally {
                this.model.gameEditor.enableViewAction("downloadLogs");
            }
        }
    }

    _onBackButtonClick() {
        this._model._gameEditor.setActiveView(Registrations.WTE_VIEW_HISTORY, [
            {
                value: {
                    fqn: Registrations.WTE_VIEW_LOGS
                }
            }
        ]);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _setLogs(newVal) {
        this._logDisplay.innerHTML = "";
        this._lineNumber = 0;
        this._logFile = "";
        if (!_.isUndefined(newVal)) {
            for (let i = 0; i < newVal.length; i++) {
                this._appendLog(newVal[i], i + 1);
            }
        }
    }

    _createLogElement(log, lln) {
        this._lineNumber = lln;
        if (!this._actionsRegistered) {
            this._registerViewActions();
            this._actionsRegistered = true;
        }

        let logFileLine = [];
        let logEntry = document.createElement("p");

        // Line number
        let logLineNumber = document.createElement("span");
        logLineNumber.classList.add("log-line-number");
        logLineNumber.innerHTML = lln;
        logFileLine.push(lln);

        // Timestamp
        let timestamp = _.get(log, "time", undefined);
        let fileTimeStamp = timestamp;
        if (!_.isUndefined(timestamp)) {
            fileTimeStamp = Date.create(timestamp).format("{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{fff}");
            timestamp = Date.create(timestamp).format("{HH}:{mm}:{ss}.{fff}");
        } else {
            timestamp = "";
        }
        let time = document.createElement("span");
        time.classList.add("timestamp");
        time.innerHTML = timestamp;
        logFileLine.push(timestamp);
        if (!this._logFileStarttime) {
            this._logFileStarttime = fileTimeStamp;
        }

        // Message
        let message = document.createElement("span");
        message.classList.add("message");
        let msg = _.get(log, "msg", "");
        message.innerHTML = msg;
        logFileLine.push(msg);

        logEntry.appendChild(logLineNumber);
        logEntry.appendChild(time);
        logEntry.appendChild(message);

        this._logFile += logFileLine.join(" ") + "\n";

        return logEntry;
    }

    _appendLog(log, lln) {
        this._logDisplay.appendChild(this._createLogElement(log, lln));
    }

    /*
    // Sneha's code
    _setLogs(newVal) {
        if (!_.isUndefined(newVal)) {
            this._registerViewActions();
            this._logsControl.setLogs(newVal);
            this._appendLogFileLine(newVal);
        }
    }

    _appendLogs(logs) {
        console.log('_appendLogs');
        console.log(logs);
        this._registerViewActions();
        this._logsControl.appendLogs(logs);
        this._appendLogFileLine(logs);
    }

    _appendLogFileLine(logs) {
        let logsArray = _.isArray(logs) ? logs : [logs];
        for(let i=0; i < logsArray.length; i++) {
            let logFileLine = this._createLogFileLine(logsArray[i]);
            this._logFile += logFileLine.join(" ") + "\n";
        }
    }

    _createLogFileLine(log) {
        let logFileLine = [];
        logFileLine.push(_.get(log, "lln", ""));
        let timestamp = _.get(log, "time", undefined);
        let fileTimeStamp = timestamp;
        if (!_.isUndefined(timestamp)) {
            fileTimeStamp = Date.create(timestamp).format("{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{fff}");
            timestamp = Date.create(timestamp).format("{HH}:{mm}:{ss}.{fff}");
        } else {
            timestamp = "";
        }
        logFileLine.push(timestamp);
        if (!this._logFileStarttime) {
            this._logFileStarttime = fileTimeStamp;
        }
        let level = document.createElement("span");
        level.classList.add("level");
        let lvl = "";
        if (_.get(log, "resp", "")) {
            lvl = "RESP";
        } else if (_.get(log, "eventType", false)) {
            if (_.get(log, "eventType") === "START" || _.get(log, "eventType") === "FINISH") {
                lvl = _.get(log, "eventType");
            } else {
                lvl = "EVENT";
            }
        }
        level.innerHTML = lvl;
        logFileLine.push(lvl);
        let msg = _.has(log, 'msg') ? log.msg : _.get(log, 'resp', '');
        logFileLine.push(msg);
        return logFileLine;
    }

     */

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = LogsViewRenderer;
